import { Component, HostListener, input, signal, Output, EventEmitter, inject, computed } from '@angular/core';
import { ChatService } from '../../../services/chat/chat.service';
import { IdentityService } from '../../../services/identity/identity.service';

@Component({
  selector: 'app-player-context-menu',
  imports: [],
  templateUrl: './player-context-menu.html',
  styleUrl: './player-context-menu.scss',
})
export class PlayerContextMenu {
  private chatService = inject(ChatService);
  private identityService = inject(IdentityService);
  public arena = this.chatService.arena;
  public state = signal<{ x: number, y: number, show: boolean }>({ x: 0, y: 0, show: false });
  
  public player = input<any>();
  public isFriend = input<boolean>(false);
  public isLobbyOwner = input<boolean>(false);
  public isFriendList = input<boolean>(false);

  public canKick = computed(() => {
    const a = this.arena();
    if (!a) return false;

    const selectedPlayer = this.player();
    if (!selectedPlayer) return false;

    const claims = this.identityService.getClaims();
    if (!claims) return false;

    const userId = claims.nameid || claims.sub || claims.name;
    const userName = claims.name || claims.unique_name;

    // Cannot kick yourself
    const selectedPlayerId = selectedPlayer.id || selectedPlayer.userId || selectedPlayer.guid;
    if (selectedPlayerId === userId || selectedPlayer.userName === userName) {
      return false;
    }

    // 1. System admin
    const isSystemAdmin = claims.role === 'admin' || claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'admin';
    if (isSystemAdmin) return true;

    // 2. Owner of the current arena (lobby or room)
    const isOwner = a.ownerGuid === userId || a.creatorId === userId || a.ownerId === userId;
    if (isOwner) return true;

    // 3. User's role in the current arena users
    const myUser = this.chatService.users().find(u => u.userName === claims.name || u.userName === claims.unique_name || u.id === userId || u.guid === userId);
    if (myUser) {
      if (a.parentGuid == null) {
        // Lobby Context: check if lobby admin
        if (myUser.role === 'admin') return true;
      } else {
        // Room Context: check if room moderator or admin
        if (myUser.role === 'admin' || myUser.role === 'moderator') return true;
      }
    }

    return false;
  });

  public canPassModerator = computed(() => {
    const a = this.arena();
    if (!a || a.parentGuid == null) return false;

    const selectedPlayer = this.player();
    if (!selectedPlayer) return false;

    const claims = this.identityService.getClaims();
    if (!claims) return false;

    const userId = claims.nameid || claims.sub || claims.name;

    // Check system admin role or arena owner role
    const isSystemAdmin = claims.role === 'admin' || claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'admin';
    const isOwner = a.ownerGuid === userId || a.creatorId === userId || a.ownerId === userId;
    if (!isSystemAdmin && !isOwner) return false;

    // Cannot pass to yourself
    const selectedPlayerId = selectedPlayer.id || selectedPlayer.userId || selectedPlayer.guid;
    if (selectedPlayerId === userId) return false;

    return true;
  });

  @Output()
  action = new EventEmitter<{ action: string, player: any }>();

  static activeMenu: PlayerContextMenu | null = null;

  public open(event: MouseEvent) {
    event.preventDefault(); // Prevent default browser menu
    event.stopPropagation(); // Prevent document:contextmenu from immediately closing this menu

    if (PlayerContextMenu.activeMenu && PlayerContextMenu.activeMenu !== this) {
      PlayerContextMenu.activeMenu.closeMenu();
    }
    PlayerContextMenu.activeMenu = this;

    this.state.set({ x: event.clientX, y: event.clientY, show: true });
  }

  // Close menu on any other click or right-click outside
  @HostListener('document:click')
  @HostListener('document:contextmenu')
  closeMenu() {
    if (this.state().show) {
      this.state.set({ ...this.state(), show: false });
      if (PlayerContextMenu.activeMenu === this) {
        PlayerContextMenu.activeMenu = null;
      }
    }
  }

  onAction(actionName: string) {
    this.action.emit({ action: actionName, player: this.player() });
    this.closeMenu();
  }

  getModalId(): string {
    const p = this.player();
    const idStr = p ? (p.id || p.userId || p.userName || 'unknown') : 'unknown';
    return 'removeFriendModal-' + idStr.toString().replace(/[^a-zA-Z0-9]/g, '');
  }
}
